import { useEffect, useRef, useState } from 'react';
import { unlockAudio } from '@/audio/context';
import { playChime } from '@/audio/chime';
import { navigate } from '@/app/router';
import { labStore } from '@/game/labProgress';
import { DIAGRAMS } from '@/content/diagrams';
import { floorIds, R_SOUND, type Floor } from '@/content/floors';
import { labBunny } from '@/content/helpers';
import { commandCenter } from '@/content/scenes';
import { strings } from '@/content/strings';
import { transmissionFor } from '@/content/transmission';
import { wordSetWith, type WordSet } from '@/content/words';
import { SettingsDialog } from '@/features/settings/SettingsDialog';
import { letterCount } from '@/game/codename';
import type { SessionSnapshot } from '@/game/practiceSession';
import {
  floorProgress,
  isUnlocked,
  revealCap,
  type MissionResult,
  type Stars,
} from '@/game/progress';
import { PRIOR } from '@/game/mastery';
import { progressStore, useProgress } from '@/game/progressStore';
import { ageBand, settingsStore, youngVoice } from '@/game/settings';
import { lettersPerWord, lettersRevealed } from '@/game/rewards';
import { useOrientation } from '@/lib/orientation';
import { CoverSpace } from '@/pixi/CoverSpace';
import { DustMotes } from '@/pixi/effects/DustMotes';
import { GlowLights } from '@/pixi/effects/GlowLights';
import { FoeCodenameCard } from '@/ui/FoeCodenameCard';
import { GameStage } from '@/ui/GameStage';
import { MicButton, type MicState } from '@/ui/MicButton';
import { MouthPicture } from '@/ui/MouthHintCard';
import { PronunciationFeedback, type AttemptStatus } from '@/ui/PronunciationFeedback';
import { ScreenEmblem } from '@/ui/ScreenEmblem';
import { TopBar } from '@/ui/TopBar';
import { ChoiceCards } from '@/ui/ChoiceCards';
import { SelfCheckCard } from '@/ui/SelfCheckCard';
import { TransmissionMonitor, type ScoutPose } from '@/ui/TransmissionMonitor';
import { WordCueCard } from '@/ui/WordCueCard';
import { MissionComplete } from './MissionComplete';
import { DebugBar } from '@/features/debug/DebugBar';
import { useDebug } from '@/game/debug';
import { setElevatorBeat } from '@/game/beats';
import { SimulatedChildPanel } from './SimulatedChildPanel';
import { useScannerSession } from './useScannerSession';
import styles from './VoiceprintScanner.module.css';
import { NOISE_ANSWERS } from '@/game/coach/noiseCheck';

const params = new URLSearchParams(window.location.search);
/** `?mic=sim` replaces the microphone with real dataset recordings (development and tests). */
const SIMULATED_MIC = params.get('mic') === 'sim';
/**
 * Development and test switches. `&voice=sim` replaces text-to-speech with a timed stand-in (no
 * speaker attached); it is the default with the simulated microphone (`&voice=web` overrides).
 * With the simulated microphone, `&silenceMs=` shortens the wait before a re-prompt.
 */
/** Simulation only: practise other words that have real recordings (`&words=rose,ring`). */
const WORDS_OVERRIDE = SIMULATED_MIC || params.get('voice') === 'sim' ? params.get('words') : null;
const SESSION_OPTIONS = {
  simulatedMic: SIMULATED_MIC,
  simulatedVoice: params.get('voice') === 'sim' || (SIMULATED_MIC && params.get('voice') !== 'web'),
  silenceMs: SIMULATED_MIC ? Number(params.get('silenceMs')) || undefined : undefined,
  echoGain: SIMULATED_MIC ? Number(params.get('echo')) || undefined : undefined,
};

/** Between letters when several are uncovered at once. */
const REVEAL_STEP_MS = 550;

const backgrounds = {
  portrait: commandCenter.portrait.background,
  landscape: commandCenter.landscape.background,
};

function micState(phase: SessionSnapshot['phase']): MicState {
  if (phase === 'listening' || phase === 'hearing') return 'listening';
  if (phase === 'talking' || phase === 'checking' || phase === 'starting') return 'busy';
  // A question is open: it is answered by tapping, not by speaking.
  if (phase === 'answering') return 'busy';
  return 'idle';
}

function status(s: SessionSnapshot): AttemptStatus {
  if (s.phase === 'listening') return 'listening';
  if (s.phase === 'hearing') return 'hearing';
  if (s.phase === 'checking') return 'checking';
  if (s.lastOutcome === 'correct' || s.phase === 'complete') return 'passed';
  if (s.lastOutcome === 'closer') return 'progress';
  if (s.lastOutcome === 'not-yet' || s.lastOutcome === 'move-on') return 'retry';
  return 'ready';
}

/** Scout listens, receives (judging, or understood), or celebrates a clear r. */
function scoutPose(s: SessionSnapshot): ScoutPose {
  if (s.transmitted === 'clear') return 'celebrating';
  if (s.transmitted || s.phase === 'checking') return 'receiving';
  return 'listening';
}

function headline(s: SessionSnapshot, sound: string): string {
  const f = strings.feedback;
  if (s.transmitting) {
    if (s.transmitted) return strings.transmission.received;
    if (s.phase === 'hearing') return f.hearing;
    if (s.phase === 'checking') return f.checking;
    return strings.transmission.sayIt;
  }
  switch (s.phase) {
    case 'loading':
      return f.loading(Math.round(s.loadProgress * 100));
    case 'off':
      return f.micOff;
    case 'starting':
      return f.starting;
    case 'error':
      return f.error;
    case 'complete':
      return f.complete;
    case 'checking':
      return f.checking;
    case 'answering':
      return f.tapAnswer;
    case 'hearing':
      return f.hearing;
    case 'listening':
      return s.lastOutcome === null || s.lastOutcome === 'prompt' ? f.yourTurn : f.listening;
    case 'talking':
      break;
  }
  switch (s.lastOutcome) {
    case 'correct':
      return f.great(sound);
    case 'closer':
      return f.closer;
    case 'not-yet':
      return f.notYet;
    case 'not-heard':
    case 'too-quiet':
      return f.notHeard;
    case 'too-long':
    case 'off-target':
      return f.justTheWord;
    case 'move-on':
      return f.newWord;
    case 'session-end':
      return f.complete;
    default:
      return f.yourTurn;
  }
}

export interface MissionOutcome {
  result: MissionResult;
  stars: Stars;
  improved: boolean;
  /** Words with a correct r in the child's best earlier mission on this floor (null: first). */
  previousBest: number | null;
  /** Attempts on this floor over all missions, this one included. */
  practised: number;
  /** Codename letters uncovered after the mission (all of them with 2 or more stars). */
  revealed: number;
  /** Floor number opened by this mission, if any. */
  unlockedFloor: number | undefined;
  /** The floor's mastery estimate (the r signal) when the mission started. */
  signalFrom: number;
  /** A clear r when transmitting the codename (undefined: no transmission this mission). */
  bonus?: boolean;
}

/**
 * One floor's mission: composes shared UI components; the conversation runs in PracticeSession.
 * Codename letters and stars are saved in the device's progress store.
 */
export function VoiceprintScanner({ floor }: { floor: Floor }) {
  const [wordSet] = useState<WordSet>(() =>
    WORDS_OVERRIDE
      ? wordSetWith(WORDS_OVERRIDE.split(','))
      : { sound: R_SOUND, words: floor.words },
  );
  const foe = floor.foe;
  const [start] = useState(() => floorProgress(progressStore.get(), floor.id));
  // The age applies from the next mission when a grown-up changes it mid-mission.
  const [options] = useState(() => ({ ...SESSION_OPTIONS, age: settingsStore.get().age }));
  const { snapshot, session, simulatedMic } = useScannerSession(wordSet, options);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const debug = useDebug();
  const orientation = useOrientation();
  const view = commandCenter[orientation];
  const sound = wordSet.sound;
  const word = wordSet.words.find((w) => w.id === snapshot.word.id) ?? wordSet.words[0];
  if (!word) throw new Error('The scanner needs at least one word');

  // Codename letters: per correct word (more for a long codename, lettersPerWord), plus per two
  // progress credits. The last letter
  // waits for 2 stars at the end of the mission (unless the floor already has them).
  const total = letterCount(foe.codename);
  const perWord = lettersPerWord(total, wordSet.words.length);
  // Once the mission is done its stars may uncover the rest (stored by missionDone): the child
  // needs to read the whole codename to transmit it.
  const stored = useProgress()[floor.id]?.revealed ?? 0;
  const revealed = Math.max(
    revealCap(
      start.stars,
      lettersRevealed(start.revealed, snapshot.successes, snapshot.progressCredits, total, perWord),
      total,
    ),
    snapshot.transmitting ? stored : 0,
  );
  useEffect(() => {
    progressStore.revealed(floor.id, revealed);
  }, [revealed, floor.id]);
  // New letters appear one at a time, each with a pop and a chime, so none goes unnoticed
  // (QA 2026-10-11: two at once, or the rest at the end, slipped by).
  const [stepped, setStepped] = useState(revealed);
  // Letters never shrink on screen beyond what is revealed (a restart shows the stored count).
  const shown = Math.min(stepped, revealed);
  const [highlight, setHighlight] = useState<number>();
  useEffect(() => {
    if (shown >= revealed) return;
    const timer = setTimeout(() => {
      setHighlight(shown);
      setStepped(shown + 1);
      playChime();
    }, REVEAL_STEP_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [revealed, shown]);

  // Mission end: record stars once (when the session completes) and show the results.
  const [outcome, setOutcome] = useState<MissionOutcome>();
  /** The results, held while the codename is transmitted. */
  const pending = useRef<MissionOutcome>(undefined);
  useEffect(() => {
    let recorded = false;
    return session.subscribe(() => {
      const snap = session.getSnapshot();
      if (recorded || snap.phase !== 'complete') return;
      recorded = true;
      const index = floorIds.indexOf(floor.id);
      const wasOpen = isUnlocked(floorIds, index + 1, progressStore.get());
      const result = session.summary();
      const earned = lettersRevealed(
        start.revealed,
        snap.successes,
        snap.progressCredits,
        total,
        perWord,
      );
      const { stars, improved, previousBest } = progressStore.missionDone(
        floor.id,
        result,
        earned,
        total,
      );
      const now = floorProgress(progressStore.get(), floor.id);
      const revealedNow = now.revealed;
      const nowOpen = isUnlocked(floorIds, index + 1, progressStore.get());
      const done: MissionOutcome = {
        result,
        stars,
        improved,
        previousBest,
        practised: now.practised ?? 0,
        revealed: revealedNow,
        unlockedFloor: !wasOpen && nowOpen && index + 1 < floorIds.length ? index + 2 : undefined,
        signalFrom: start.mastery?.p ?? PRIOR,
      };
      // The whole codename uncovered: the child transmits it to Scout first (epic #44), then the
      // results open (below, when the transmission ends).
      if (revealedNow >= total) {
        pending.current = done;
        void session.transmit(transmissionFor(foe, ageBand(options.age)));
      } else setOutcome(done);
    });
  }, [session, floor.id, start, total, perWord, foe, options.age]);

  const transmitted = snapshot.transmitted;
  useEffect(() => {
    const done = pending.current;
    if (!transmitted || !done) return;
    pending.current = undefined;
    if (transmitted === 'clear') progressStore.bonus(floor.id);
    // A moment to see Scout's reply before the results.
    const timer = setTimeout(() => {
      setOutcome({ ...done, bonus: transmitted === 'clear' });
    }, 900);
    return () => {
      clearTimeout(timer);
    };
  }, [transmitted, floor.id]);

  // Every judged attempt updates the floor's mastery estimate (the scorer's own verdict, not the
  // coach's shaping; the estimate itself allows for the scorer's errors).
  const young = youngVoice(options.age);
  const mastery = useProgress()[floor.id]?.mastery;
  useEffect(() => {
    const judged = snapshot.result;
    if (judged?.heard) progressStore.attempt(floor.id, judged.accepted, young);
  }, [snapshot.result, floor.id, young]);

  const toElevator = () => {
    void session.stopMic();
    // The story moves on at the elevator: the next foe, or the whole building unmasked.
    if (outcome && outcome.stars >= 2) {
      if (floorIds.indexOf(floor.id) === floorIds.length - 1) setElevatorBeat('allDone');
      else if (outcome.unlockedFloor) setElevatorBeat('next');
    }
    // The cat's mask is off: Alex asks to talk in private (alex-lab.md), once.
    if (
      outcome &&
      outcome.stars >= 2 &&
      floor.id === floorIds[0] &&
      labStore.get().reveal === 'untold'
    ) {
      navigate({ page: 'lab' });
      return;
    }
    navigate({ page: 'elevator' });
  };

  // Per-sound verdicts for the word: the target sound by the classifier's decision. Other sounds
  // only light up when they sounded right; they never turn amber, so feedback stays on the target.
  // Hidden while a word steps down to its syllable (the strip shows the word); a practice syllable
  // that is the target itself (floor 1: rah, roh) lights up like a word (QA 2026-10-11).
  const result = snapshot.target.text === word.text ? snapshot.result : null;
  const results = result?.phonemes.map((p, i) =>
    i === result.targetIndex ? result.accepted : p.score >= 0.5 ? true : undefined,
  );

  const micOn = session.isMicOn;
  const hint = snapshot.phase === 'error' ? null : snapshot.hint;
  const choosing = snapshot.choices !== null;
  // The syllable step for a word ("rah" for rock): the card shows the syllable to say.
  const stepping = snapshot.target.kind === 'syllable' && word.kind !== 'syllable';
  const caption =
    snapshot.phase === 'error'
      ? (snapshot.error ?? '')
      : snapshot.trying
        ? strings.hint.yourTurn(snapshot.trying)
        : choosing && snapshot.phase !== 'talking'
          ? strings.speech.chooseNext
          : snapshot.target.kind === 'syllable' && snapshot.phase !== 'talking'
            ? strings.feedback.practiseSyllable(snapshot.target.text)
            : snapshot.caption;

  return (
    <GameStage
      backgrounds={backgrounds}
      scene={
        <>
          <CoverSpace
            key={orientation}
            width={view.background.width}
            height={view.background.height}
          >
            <GlowLights lights={view.lights} />
          </CoverSpace>
          <DustMotes />
        </>
      }
    >
      <div className={styles.screen}>
        <TopBar
          onBack={toElevator}
          onSettings={() => {
            void session.stopMic();
            setSettingsOpen(true);
          }}
        />
        <div className={styles.emblem}>
          <ScreenEmblem
            first={strings.scanner.titleTop}
            second={strings.scanner.titleBottom}
            width={orientation === 'landscape' ? 310 : 280}
          />
        </div>

        <div className={styles.gap} />
        <div className={styles.row}>
          <div className={styles.cue}>
            {snapshot.transmitting ? (
              <TransmissionMonitor
                pose={scoutPose(snapshot)}
                onHear={
                  transmitted
                    ? undefined
                    : () => {
                        void session.hearWord();
                      }
                }
                onSend={
                  transmitted
                    ? undefined
                    : () => {
                        void session.sendCodename();
                      }
                }
              />
            ) : snapshot.noiseQuestion ? (
              <SelfCheckCard
                title={strings.speech.quieterSpot}
                options={[NOISE_ANSWERS.moving.label, NOISE_ANSWERS.stay.label]}
                answerable={snapshot.answerable}
                onAnswer={(label) => {
                  void session.answerNoise(label === NOISE_ANSWERS.moving.label);
                }}
              />
            ) : snapshot.selfCheck ? (
              <SelfCheckCard
                options={snapshot.selfCheck.options}
                answerable={snapshot.answerable}
                onAnswer={(w) => {
                  void session.answerSelfCheck(w);
                }}
                onSkip={() => {
                  void session.dismissQuestion();
                }}
              />
            ) : snapshot.choices ? (
              <ChoiceCards
                options={snapshot.choices.map((c) => {
                  const w = wordSet.words.find((x) => x.id === c.id) ?? word;
                  return { id: c.id, word: w.text, picture: w.picture };
                })}
                onChoose={(id) => {
                  void session.choose(id);
                }}
              />
            ) : (
              <WordCueCard
                id={stepping ? `${word.id}:step` : word.id}
                word={stepping || hint ? snapshot.target.text : word.text}
                // A placement cue turns the same card to the mouth picture and the step being
                // said, until the next judged attempt: part of the practice, not a window over it.
                coaching={
                  hint && {
                    id: `${hint.insteadOf ?? ''}>${hint.shows}`,
                    picture: (
                      <MouthPicture
                        shows={DIAGRAMS[hint.shows]}
                        insteadOf={hint.insteadOf && DIAGRAMS[hint.insteadOf]}
                      />
                    ),
                    line:
                      (snapshot.hintStep === null
                        ? undefined
                        : hint.steps?.[snapshot.hintStep]?.text) ?? DIAGRAMS[hint.shows].look,
                    trying: snapshot.trying,
                    onReplay: () => {
                      void session.replayCue();
                    },
                  }
                }
                picture={word.picture}
                mystery={!snapshot.exercise.revealed}
                hint={
                  stepping
                    ? strings.speech.easierStep(word.text)
                    : snapshot.exercise.kind === 'riddle' && !snapshot.exercise.revealed
                      ? strings.speech.solveRiddle
                      : snapshot.exercise.kind === 'complete'
                        ? strings.speech.finishSentence
                        : strings.speech.sayThisWord
                }
                onHear={() => {
                  void session.hearWord();
                }}
                onHelp={() => {
                  void session.help();
                }}
              />
            )}
          </div>

          <div className={styles.rowGap} />
          <MicButton
            state={micState(snapshot.phase)}
            label={micOn ? strings.speech.micStop : strings.speech.micStart}
            size={orientation === 'landscape' ? 128 : 112}
            disabled={snapshot.phase === 'loading' || snapshot.phase === 'starting'}
            pressed={micOn}
            onPress={() => {
              if (!micOn && !simulatedMic) unlockAudio();
              void session.toggleMic();
            }}
          />

          <div className={styles.rowGap} />
          <PronunciationFeedback
            key={word.id}
            // While choosing there is no single target yet: no sound strip.
            segments={(choosing || snapshot.transmitting ? [] : word.segments).map((s) => ({
              // An unsolved riddle shows only the practised sound: a clue, not the answer.
              letters: snapshot.exercise.revealed || s.phoneme === sound.phoneme ? s.letters : '?',
              target: s.phoneme === sound.phoneme,
            }))}
            status={status(snapshot)}
            headline={headline(snapshot, sound.label)}
            caption={caption}
            results={results}
            level={(listener) => session.onLevel(listener)}
            // Scout is on the monitor while the codename is transmitted; no second helper.
            {...(snapshot.transmitting ? {} : { helper: labBunny.portraits.clipboard })}
          />
        </div>

        <div className={styles.gap} />
        <div className={styles.foe}>
          <FoeCodenameCard
            foe={foe}
            revealedCount={shown}
            highlight={highlight}
            status={shown >= total ? strings.codename.complete : undefined}
            signal={mastery?.p}
            layout={orientation === 'landscape' ? 'inline' : 'stacked'}
          />
        </div>
      </div>
      {simulatedMic && <SimulatedChildPanel mic={simulatedMic} snapshot={snapshot} />}
      {debug && !outcome && <DebugBar session={session} />}
      {settingsOpen && (
        <SettingsDialog
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
      )}
      {outcome && (
        <MissionComplete
          floor={floor}
          outcome={outcome}
          onElevator={toElevator}
          onAgain={() => {
            navigate({ page: 'floor', floorId: floor.id, run: String(Date.now()) });
          }}
        />
      )}
    </GameStage>
  );
}
